import type { NightmareType } from './nightmares.ts';
import type { DamageType } from './damage.ts';
import { sec } from './units.ts';

/*
 * The Chasm (la Sima, docs/SIMA.md): a crypt with no known bottom under the deep crypt. One floor at a time, each
 * harder than the last and from a different stratum; a guardian every fifth floor; the group's mark is the deepest
 * floor anyone of it finished. Everything here is data: the sim reads it, the render and the client name it.
 */

/** The twelve strata the Chasm cuts through (docs/SIMA.md, «Los doce estratos»). The first three are the deep crypt's. */
export type StratumId = 'ossuary' | 'marrow' | 'glass' | 'cisterns' | 'buried' | 'grotto' | 'frost' | 'geode' | 'archive' | 'forge' | 'nest' | 'ribs';
export const STRATA: StratumId[] = ['ossuary', 'marrow', 'glass', 'cisterns', 'buried', 'grotto', 'frost', 'geode', 'archive', 'forge', 'nest', 'ribs'];

/** The Chasm's own creatures (one per new stratum; the nest brings the weaver's brood, which exists). */
export type SimaCreature = 'leech' | 'rootwight' | 'puffball' | 'rime' | 'shardling' | 'scribe' | 'molten' | 'boneworm';
export const SIMA_CREATURES: SimaCreature[] = ['leech', 'rootwight', 'puffball', 'rime', 'shardling', 'scribe', 'molten', 'boneworm'];

/** The new guardians (the Warden, the magma lord and the colossus guard the first three strata). */
export type SimaGuardian = 'leechMother' | 'rootking' | 'sporeMother' | 'frostLady' | 'prism' | 'archivist' | 'bellows' | 'broodQueen' | 'marrowWorm';
export const SIMA_GUARDIANS: SimaGuardian[] = ['leechMother', 'rootking', 'sporeMother', 'frostLady', 'prism', 'archivist', 'bellows', 'broodQueen', 'marrowWorm'];

/**
 * A stratum's hazards on the floor (sim zone kind 'sima', `haz`). Two shapes: fixed (water, deep water, rime,
 * ink, web: there all the floor long) and marked-then-strikes (niches, roots, beams, hammers, rockfalls: a ring
 * on the floor `warn` ticks before it lands). Spores drift. Embers (the sign) and molten pools (a molten's trail) are
 * short fires; a quake is a guardian's marked strike (the rootking's roots, the worm's surfacing).
 */
export type SimaHazard = 'niche' | 'water' | 'deep' | 'roots' | 'spores' | 'rime' | 'beam' | 'ink' | 'hammer' | 'web' | 'rockfall' | 'embers' | 'molten' | 'quake';

/** The rules a floor can carry from the tenth on (docs/SIMA.md, «Signos»). */
export type SimaSign = 'procession' | 'embers' | 'frost' | 'pair' | 'echo' | 'rage' | 'ward' | 'gloom';
export const SIMA_SIGNS: SimaSign[] = ['procession', 'embers', 'frost', 'pair', 'echo', 'rage', 'ward', 'gloom'];
/** Signs that never come together (they step on each other). */
export const SIGN_CLASH: [SimaSign, SimaSign][] = [['embers', 'frost']];
/** The damage types a «ward» sign can name. */
export const WARD_TYPES: DamageType[] = ['physical', 'fire', 'frost', 'lightning', 'holy', 'shadow'];

/** How deep: the corruption grades as bands of ten floors (docs/SIMA.md, «Las bandas»). */
export type SimaBand = 'shadow' | 'ink' | 'ash' | 'obsidian' | 'abyss';
export const SIMA_BANDS: SimaBand[] = ['shadow', 'ink', 'ash', 'obsidian', 'abyss'];

export interface StratumDef {
  /** The guardian of its threshold floors. */
  guardian: 'warden' | 'magmaLord' | 'colossus' | SimaGuardian;
  /** Its own creature, if any, and the nightmares that come with it (weights; filtered by the group's era). */
  creature?: SimaCreature;
  bestiary: Partial<Record<NightmareType | SimaCreature, number>>;
  /** Its hazard on the floor (the first three: the deep crypt's own). */
  hazard?: SimaHazard;
  /** The gear slot a run ending here favours (docs/SIMA.md, «Ranura favorecida»). */
  slot: 'weapon' | 'offhand' | 'head' | 'armor' | 'boots' | 'amulet' | 'ring';
  /** Its light, for the render and the card (never gold or beige: docs/SIMA.md, «Visuales»). */
  color: string;
}

export const STRATUM: Record<StratumId, StratumDef> = {
  ossuary: {
    guardian: 'warden',
    bestiary: { goblin: 3, skeleton: 3, wolf: 2, stalker: 1, slinger: 2 },
    hazard: 'niche',
    slot: 'armor',
    color: '#8fa3c0',
  },
  marrow: {
    guardian: 'magmaLord',
    bestiary: {
      skeleton: 3,
      wraith: 2,
      slime: 2,
      gargoyle: 1,
      ogre: 1,
      wyrmling: 1,
    },
    slot: 'offhand',
    color: '#ff7a2f',
  },
  glass: {
    guardian: 'colossus',
    bestiary: {
      skeleton: 2,
      gargoyle: 2,
      wraith: 2,
      stalker: 2,
      mourner: 1,
      ogre: 1,
    },
    slot: 'weapon',
    color: '#a77bff',
  },
  cisterns: {
    guardian: 'leechMother',
    creature: 'leech',
    bestiary: { leech: 4, slime: 2, wraith: 2, skeleton: 2 },
    hazard: 'deep',
    slot: 'boots',
    color: '#5fd0b8',
  },
  buried: {
    guardian: 'rootking',
    creature: 'rootwight',
    bestiary: { rootwight: 2, wolf: 3, goblin: 2, troll: 1, stalker: 1 },
    hazard: 'roots',
    slot: 'offhand',
    color: '#7fc25a',
  },
  grotto: {
    guardian: 'sporeMother',
    creature: 'puffball',
    bestiary: { puffball: 3, moth: 2, slime: 2, goblin: 2 },
    hazard: 'spores',
    slot: 'head',
    color: '#4fd6ff',
  },
  frost: {
    guardian: 'frostLady',
    creature: 'rime',
    bestiary: { rime: 3, wraith: 2, wolf: 2, skeleton: 2 },
    hazard: 'rime',
    slot: 'armor',
    color: '#cfe8ff',
  },
  geode: {
    guardian: 'prism',
    creature: 'shardling',
    bestiary: { shardling: 3, gargoyle: 2, beetle: 1, stalker: 2 },
    hazard: 'beam',
    slot: 'amulet',
    color: '#c56cff',
  },
  archive: {
    guardian: 'archivist',
    creature: 'scribe',
    bestiary: { scribe: 2, mourner: 1, skeleton: 3, wraith: 2, bonesinger: 1 },
    hazard: 'ink',
    slot: 'head',
    color: '#9aa7b4',
  },
  forge: {
    guardian: 'bellows',
    creature: 'molten',
    bestiary: { molten: 2, ogre: 1, beetle: 2, skeleton: 2, goblin: 2 },
    hazard: 'hammer',
    slot: 'weapon',
    color: '#ff5a3a',
  },
  nest: {
    guardian: 'broodQueen',
    bestiary: { weaver: 2, spiderling: 4, moth: 1, stalker: 1 },
    hazard: 'web',
    slot: 'boots',
    color: '#d9c8ff',
  },
  ribs: {
    guardian: 'marrowWorm',
    creature: 'boneworm',
    bestiary: { boneworm: 4, skeleton: 2, troll: 1, ogre: 1 },
    hazard: 'rockfall',
    slot: 'ring',
    color: '#d0464f',
  },
};

/** Creatures of the Chasm that come regardless of the group's era (docs/SIMA.md: the nest's brood is the only exception besides its own). */
export const SIMA_ANY_ERA: (NightmareType | SimaCreature)[] = ['weaver', 'spiderling', ...SIMA_CREATURES];

export const SIMA = {
  /** Opens once the group won the deep crypt to its bottom (a hero has its seal, `deepWon`), and the group is at level `minGroup`. */
  minGroup: 8,
  maxHeroes: 8,
  /** A threshold floor (a guardian's hall instead of a captain's) every `every` floors. */
  threshold: 5,
  /** Floors in a band (one more sign each band, up to `maxSigns`). */
  band: 10,
  maxSigns: 3,
  /** From the abyss band on one of the signs is always the echo. */
  abyssSign: 'echo' as SimaSign,
  /** A campaign week (as the Furrows): a new seed for the order of the strata and the signs. */
  weekDays: 7,
  /**
   * Per floor, compounding (docs/SIMA.md, «Dificultad»): nightmares' health +10 %, their blows +6 %. The sim reads
   * `SIMA_SCALE` (precomputed below up to `tableFloors`; past it, the last value).
   */
  hpStepBp: 1000,
  dmgStepBp: 600,
  tableFloors: 120,
  /**
   * The floor-one base: what the deep crypt's first floor puts before a party of level 15 today (its level scaling in,
   * fixed from then on). Level 15 is 14 levels over 1: perLevelBp 3600 up to `fromLevel` 8 (7 levels), then 2000 count.
   */
  baseCountBp: 10000 + 7 * 3600 + 7 * 2000,
  baseHpBp: 10000 + 7 * 4000,
  baseDmgBp: 10000 + 7 * 3600 + 7 * 6000,
  /** And the guardians' health: the deep crypt's level scaling at 15 (perLevelBp all the way). */
  baseLevelBp: 10000 + 14 * 3600,
  /** By heroes, 1 to 8 (past 4, BIG_PARTY): the deep crypt's. */
  partyBp: [0, 10000, 15000, 28000, 52000],
  /** Nightmare points per wave: [fighting room, hazard room]. */
  roomPoints: [22, 14] as [number, number],
  /** Ink (grade 2) chance by band. */
  inkBp: [2500, 4500, 6000, 7000, 8000],
  clearHealBp: 3500,
  /** The captain: the stratum's big one (or the era's), this much tougher; elites before it grow with the party. */
  captainHpBp: 20000,
  captainDmgBp: 14000,
  /** Guardians' health by party size (1 to 8; past 4, BIG_PARTY), before the floor's scaling; and their blows. */
  guardianHp: [0, 2600, 3300, 7400, 17500],
  guardianDmgBp: 16000,
  /** The deep crypt's guardians come in at what the deep crypt gives them, by stratum (bp of `guardianHp`). */
  oldGuardianHpBp: {
    warden: 8000,
    magmaLord: 16000,
    colossus: 11000,
  } as Record<'warden' | 'magmaLord' | 'colossus', number>,
  /** Stairs and the way out, as in the deep crypt. */
  chooseTicks: 24,
  stairsRange: 900,
  /** Hazard rooms: a reinforcement every `pressEvery` (never in fighting rooms), of this many points per hero. */
  pressEvery: sec(22),
  pressPoints: 5,
  /** Map slots: the two floors in play sit this far apart (hex) either side of the centre. */
  slotSpan: 26,
};

/**
 * Health and damage multipliers by floor (bp, index = floor − 1), precomputed so the sim only multiplies by a value
 * from a table (docs/SPEC.md §3). Integer compounding: each step rounds down.
 */
const table = (step: number): number[] => {
  const t: number[] = [10000];
  for (let i = 1; i < SIMA.tableFloors; i++) {
    const prev = t[i - 1] as number;
    t.push(prev + Math.floor((prev * step) / 10000));
  }
  return t;
};
export const SIMA_SCALE = {
  hp: table(SIMA.hpStepBp),
  dmg: table(SIMA.dmgStepBp),
};
/**
 * A floor's multiplier (bp), from the table; past its last floor it stays there. By then a nightmare has tens of
 * thousands of times its health (no party gets that far), and the sim's products stay exact in a double.
 */
export const simaScale = (t: number[], floor: number): number => t[Math.max(0, Math.min(t.length, floor) - 1)] as number;

/**
 * The hazards (docs/SIMA.md, «Los doce estratos»). Ticks are 12 a second. Fixed ones last the floor; marked ones
 * strike `warn` ticks after their ring shows, `every` apart per spot (each spot its own phase).
 */
export const SIMA_HAZARD = {
  /** Ossuary: niches in the walls; a marked one spills `count` skeletons. */
  niche: { every: sec(14), warn: sec(2), count: 2, perRoom: 3 },
  /** Cisterns: shallow water slows heroes; dark deep pools let leeches out. */
  water: { slowBp: 3000 },
  deep: { every: sec(9), count: 3, radius: 1300, perRoom: 2 },
  /** Buried wood: a green ring, then roots hold whoever is still inside. */
  roots: {
    every: sec(7),
    warn: 18,
    radius: 1100,
    holdTicks: sec(2),
    perRoom: 2,
    damage: 10,
  },
  /** Grotto: clouds of spores drifting slowly; inside, a hero falls asleep. */
  spores: {
    radius: 1200,
    speed: 120,
    sleepTicks: sec(2.5),
    immune: sec(5),
    perRoom: 2,
  },
  /** Frost veins: rime on the floor chills; `stacks` chills freeze. */
  rime: {
    radius: 1100,
    chillEvery: 12,
    stacks: 4,
    freezeTicks: 18,
    slowBp: 2000,
    perRoom: 3,
  },
  /** Geode: beams between crystals light up on a rhythm. */
  beam: { every: sec(5), warn: 18, width: 450, damage: 40, perRoom: 2 },
  /** Archive: pools of ink that grow; inside, cursed. */
  ink: {
    grow: 6,
    max: 1800,
    start: 600,
    curseTicks: sec(4),
    healBp: 5000,
    perRoom: 3,
  },
  /** Dead forge: trip hammers on a rhythm. */
  hammer: {
    every: sec(4),
    warn: 18,
    radius: 800,
    damage: 55,
    stun: 9,
    perRoom: 3,
  },
  /** Nest: webs slow; stepping on one calls spiderlings. */
  web: { radius: 1000, slowBp: 4000, calls: 2, cooldown: sec(8), perRoom: 3 },
  /** Ribs: rocks fall on marked circles; bone worms come out where they fall. */
  rockfall: {
    every: sec(6),
    warn: 18,
    radius: 900,
    damage: 45,
    worms: 1,
    perRoom: 3,
  },
};

/** A hero held by roots: it can strike and cast but not walk (docs/SIMA.md: the one new condition). */
export const HELD = { graceTicks: sec(3) };

/** The signs' numbers. */
export const SIMA_SIGN = {
  procession: { mourners: 1 },
  embers: { radius: 800, ticks: sec(3), dmg: 10 },
  frost: { stacks: 1 },
  pair: { hpBp: 7000 },
  echo: { hpBp: 5000 },
  rage: { atBp: 5000, dmgBp: 13000 },
  ward: { resistBp: 4000 },
  /** Gloom: how near the fog comes (render only; the sim does not see it). */
  gloom: { fogBp: 5500 },
};

/**
 * The new guardians (docs/SIMA.md, «Guardianes»): one rule each, read in three seconds. Their health comes from
 * `SIMA.guardianHp` and the floor; these are their moves.
 */
export const SIMA_BOSS = {
  /** «Pegale cuando baja el agua.» In the pool she mends; every `every` the water drains for `low` and she is bare. */
  leechMother: {
    every: sec(20),
    low: sec(6),
    mendBpPerSec: 300,
    takenInWaterBp: 2000,
    leeches: 4,
  },
  /** «Cuando hunde las raíces, salí del círculo.» */
  rootking: {
    every: sec(10),
    warn: 24,
    radius: 2600,
    holdTicks: sec(2.5),
    squeeze: 20,
  },
  /** «Cuando se hincha, alejate.» She swells for `swell`, then bursts in a ring of spores (sleep). */
  sporeMother: {
    every: sec(12),
    swell: sec(3),
    radius: 3000,
    sleepTicks: sec(3),
    takenSwellingBp: 5000,
  },
  /** «Si encierra a uno en hielo, los demás rompen el hielo.» The ice has its own health; alone it breaks after `alone`. */
  frostLady: {
    every: sec(11),
    iceHp: 120,
    alone: sec(4),
    maxTicks: sec(6),
    breath: { every: sec(7), range: 3500, arc: 90, dmg: 30 },
  },
  /** «Los cristales lo cargan: rompelos.» Each standing crystal adds a sweeping beam; while any stands it takes `shieldedBp`. */
  prism: {
    crystals: 3,
    crystalHp: 260,
    every: sec(6),
    warn: 18,
    width: 500,
    damage: 45,
    shieldedBp: 3000,
  },
  /** «Cuando lee, pegale fuerte hasta cortarlo.» Reading for `read` calls `calls` nightmares unless its bar is beaten down. */
  archivist: { every: sec(13), read: sec(4), bar: 900, calls: 4 },
  /** «Cuando sopla, ponete detrás de un yunque.» It draws breath (pulls) then blows a cone of fire; anvils shelter. */
  bellows: {
    every: sec(10),
    inhale: sec(2),
    pull: 90,
    range: 9000,
    arc: 110,
    dmg: 80,
    anvils: 3,
  },
  /** «Rompé los capullos antes de que se abran.» */
  broodQueen: { every: sec(8), hatch: sec(6), cocoonHp: 150, brood: 5 },
  /** «Se hunde y sale al final de la grieta: esperalo al costado.» */
  marrowWorm: {
    every: sec(10),
    dive: sec(3),
    radius: 1600,
    dmg: 70,
    dazed: sec(3),
    crack: 6,
  },
};

/** The new creatures' numbers (their stats live in NIGHTMARES; these are their moves). */
export const SIMA_CREATURE = {
  /** Its blow roots the hero. */
  rootwight: { holdTicks: 18, every: sec(5) },
  /** Its death bursts in spores that put to sleep. */
  puffball: { radius: 1200, sleepTicks: sec(2), immune: sec(5) },
  /** Where it crawls rime stays. */
  rime: { every: sec(2), radius: 700, ticks: sec(6) },
  /** It writes a rune under a hero: after `warn`, whoever stands on it is cursed. */
  scribe: {
    every: sec(5),
    warn: 24,
    range: 6500,
    radius: 700,
    curseTicks: sec(5),
    healBp: 5000,
    dmg: 15,
  },
  /** Molten metal behind it as it walks; frost makes it brittle. */
  molten: {
    every: sec(2),
    radius: 700,
    ticks: sec(4),
    dmg: 12,
    brittleTakenBp: 15000,
    brittleTicks: sec(4),
  },
};

/** What a floor finished adds to the run's chest, growing with the floor and capped at `capFloor` (docs/SIMA.md, «Botín»). */
export const SIMA_CHEST = {
  xp: 300,
  xpPerFloor: 40,
  dust: 50,
  dustPerFloor: 8,
  embers: 1,
  shardsFrom: 10,
  shards: 1,
  capFloor: 30,
  /** Falling keeps this share (and half the threshold pieces, the best first). */
  fallBp: 5000,
};

/** The piece's power (docs/EQUIPO.md §4): level + `base` + one every `per` floors, up to `cap`. */
export const SIMA_POWER = { base: 2, per: 3, cap: 10 };
/** The Chasm's piece of the day: rare from `rareFrom`; very rare chance from `veryRareFrom` if its power reaches 20. */
export const SIMA_LOOT = {
  rareFrom: 10,
  veryRareFrom: 20,
  veryRareBp: 200,
  veryRarePerFloorBp: 50,
  veryRareCapBp: 1200,
  relicFrom: 15,
  relicBp: 2500,
};

/** The week's chest, by the group's mark of the week (docs/SIMA.md, «Por semana»). */
export const SIMA_WEEK_CHEST = [
  {
    from: 1,
    xp: 600,
    dust: 120,
    embers: 0,
    shards: 0,
    piece: null,
    relicBp: 0,
  },
  {
    from: 10,
    xp: 1200,
    dust: 220,
    embers: 3,
    shards: 3,
    piece: 'uncommon',
    relicBp: 0,
  },
  {
    from: 20,
    xp: 2000,
    dust: 320,
    embers: 5,
    shards: 5,
    piece: 'rare',
    relicBp: 2500,
  },
  {
    from: 30,
    xp: 3000,
    dust: 420,
    embers: 6,
    shards: 6,
    piece: 'rare',
    relicBp: 4000,
  },
] as const;

/** The winch in the bastion grows (docs/SIMA.md, «Lo que se ve en el bastión»). */
export const SIMA_TROPHY = { pulley: 10, banners: 25, smoke: 40 };
